import { useEffect, useState } from 'react';

/**
 * Counts a duration down to zero, once per second.
 *
 * Seeded from a DURATION rather than a deadline, so the device clock is never consulted — the api sends
 * seconds remaining for exactly that reason, and a skewed kiosk clock cannot corrupt the number shown.
 *
 * The trade this accepts: a tab throttled by the browser drifts, so the countdown can read low. That is the
 * safe direction — the candidate is invited back a little late rather than a little early into another 429 —
 * and the api re-checks on the next attempt regardless, so this number is a courtesy, never the gate.
 */
export function useCountdown(initialSeconds: number): number {
  const [remaining, setRemaining] = useState(initialSeconds);
  const [seeded, setSeeded] = useState(initialSeconds);

  /**
   * Re-seeds when a new wait arrives — a second block must restart the countdown, not continue the old one.
   *
   * Adjusted during render rather than in an effect. React supports this and it is the documented way to
   * reset state when a prop changes; the effect form renders once with the stale value first, which here
   * means a visible flash of the previous countdown at the moment the new one starts.
   */
  if (seeded !== initialSeconds) {
    setSeeded(initialSeconds);
    setRemaining(initialSeconds);
  }

  useEffect(() => {
    if (remaining <= 0) return;

    const timer = window.setInterval(() => {
      setRemaining((current) => (current <= 1 ? 0 : current - 1));
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [remaining]);

  return remaining;
}
